import { type AnyAction } from 'redux';
import {
	call,
	put,
	takeLatest,
	type CallEffect,
	type ForkEffect,
	type PutEffect,
	type SelectEffect
} from 'redux-saga/effects';
import { InspirationsActionType } from './enums';

import {
	addInspiration,
	addInspirationItemMapping,
	deleteInspiration,
	deleteInspirationItemMapping,
	fetchAllInspirations,
	fetchInspirationDetails,
	fetchInspirationProductMapping,
	fetchShortlistedInspirations,
	removeShortlistedInspiration,
	shortlistInspiration,
	updateInspirationItem,
	updateItemInclusionStatus
} from './api';
import { transformUploadData, uploadDocumentArr } from '@utilities';
import { activateDocument } from '../products/api';

type InspirationsSagaEffect = Generator<CallEffect<any> | PutEffect<any> | SelectEffect> | any;

type InspirationsSagaFormEffect = Generator<ForkEffect<void>>;

const transformedRequest = values => {
	const req = {
		...values,
		status: values?.selectedFilter?.status
	};

	delete req?.selectedFilter;

	return req;
};

export function* fetchAllInspirationsEffect(action: AnyAction): InspirationsSagaEffect {
	const { offset, pageSize, errorCB, filters, isAppend } = action.payload;

	const updatedRequest = transformedRequest(filters);

	try {
		const { data }: any = yield call(fetchAllInspirations, {
			pageSize,
			offset,
			filters: {
				...updatedRequest,
				...(filters?.leadId && { leadId: filters.leadId })
			}
		});

		yield put({ type: InspirationsActionType.STORE_ALL_INSPIRATIONS, payload: { ...data, isAppend } });
	} catch (e: any) {
		yield put({
			type: InspirationsActionType.ERROR_ALL_INSPIRATIONS,
			payload: {
				e
			}
		});
		errorCB(e?.message);
	}
}

export function* fetchInspirationDetailsEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchInspirationDetails, inspirationId);

		yield put({ type: InspirationsActionType.STORE_INSPIRATION_DETAILS, payload: data });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* addInspirationEffect(action: AnyAction): InspirationsSagaEffect {
	const { formData, errorCB, successCB } = action.payload;
	const currentFileType = 'MEDIA';

	const transformedPayload = transformUploadData(formData.document, currentFileType);
	const newFormData = {
		...formData,
		...transformedPayload
	};
	delete newFormData.transformedData;

	const transformedObject = {
		...newFormData,
		document: {
			referenceType: newFormData.document[0].referenceType,
			category: newFormData.document[0].category,
			subCategory: newFormData.document[0].subCategory,
			attachments: newFormData.document[0].files
				.filter(file => file.fileName !== newFormData.coverImage.file)
				.map(file => ({
					file: file.fileName
				}))
		}
	};

	try {
		const { data } = yield call(addInspiration, transformedObject);
		const { document } = data;

		const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [document]);
		yield call(async () => await Promise.all(arrPromises));

		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchInspirationProductMappingEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationId, errorCB } = action.payload;

	const requestBody = {
		offset: 0,
		pageSize: 100,
		filters: {
			inspirationId: inspirationId
		}
	};
	try {
		const data: any = yield call(fetchInspirationProductMapping, requestBody);

		yield put({ type: InspirationsActionType.STORE_INSPIRATION_PRODUCT_MAPPINGS, payload: data.data });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* updateItemInclusionStatusEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationItemMappingId, updatedInclusionStatus, errorCB, successCB, finalCB } = action.payload;
	try {
		yield call(updateItemInclusionStatus, inspirationItemMappingId, {
			inclusion_status: updatedInclusionStatus
		});
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* deleteInspirationItemMappingEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationItemMappingId, errorCB, successCB, finalCB } = action.payload;
	try {
		yield call(deleteInspirationItemMapping, { ids: [inspirationItemMappingId] });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* addInspirationItemMappingEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationItemMapping, inspirationId, errorCB, successCB, finalCB } = action.payload;

	try {
		yield call(addInspirationItemMapping, inspirationId, { inspiration_item_mapping: inspirationItemMapping });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* deleteInspirationEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationId, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteInspiration, inspirationId);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchShortlistedInspirationsEffect(action: AnyAction): InspirationsSagaEffect {
	const { leadID, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchShortlistedInspirations, leadID);
		yield put({ type: InspirationsActionType.STORE_SHORTLISTED_INSPIRATIONS, payload: data });
	} catch (e: any) {
		yield put({
			type: InspirationsActionType.ERROR_SHORTLISTED_INSPIRATIONS,
			payload: {
				e
			}
		});
		errorCB(e?.message);
	}
}

export function* shortlistInspirationEffect(action: AnyAction): InspirationsSagaEffect {
	const { leadID, inspirationId, errorCB, successCB, finalCB } = action.payload;
	try {
		yield call(shortlistInspiration, leadID, inspirationId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* removeShortlistedInspirationEffect(action: AnyAction): InspirationsSagaEffect {
	const { inspirationId, errorCB, successCB, finalCB } = action.payload;
	try {
		yield call(removeShortlistedInspiration, { ids: [inspirationId] });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* updateInspirationItemEffect(action: AnyAction): InspirationsSagaEffect {
	const { values, inspirationId, errorCB, successCB, finalCB } = action.payload;
	const transformedRequestObject = {
		...values
	};
	try {
		yield call(updateInspirationItem, inspirationId, transformedRequestObject);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchAllInspirationsSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.FETCH_ALL_INSPIRATIONS, fetchAllInspirationsEffect);
}
export function* fetchInspirationDetailsSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.FETCH_INSPIRATION_DETAILS, fetchInspirationDetailsEffect);
}

export function* addInspirationSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.ADD_INSPIRATION, addInspirationEffect);
}

export function* fetchInspirationProductMappingSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.FETCH_INSPIRATION_PRODUCT_MAPPINGS, fetchInspirationProductMappingEffect);
}

export function* updateItemInclusionStatusSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.UPDATE_ITEM_MAPPING_INCLUSION_STATUS, updateItemInclusionStatusEffect);
}
export function* deleteInspirationItemMappingSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.DELETE_INSPIRATION_ITEM_MAPPING, deleteInspirationItemMappingEffect);
}

export function* addInspirationItemMappingSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.ADD_INSPIRATION_ITEM_MAPPING, addInspirationItemMappingEffect);
}

export function* deleteInspirationSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.DELETE_INSPIRATION, deleteInspirationEffect);
}

export function* fetchShortlistedInspirationSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.FETCH_SHORTLISTED_INSPIRATIONS, fetchShortlistedInspirationsEffect);
}

export function* shortlistInspirationSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.SHORTLIST_INSPIRATION, shortlistInspirationEffect);
}

export function* removeShortlistedInspirationSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.REMOVE_SHORTLISTED_INSPIRATION, removeShortlistedInspirationEffect);
}

export function* updateInspirationItemSaga(): InspirationsSagaFormEffect {
	yield takeLatest(InspirationsActionType.UPDATE_INSPIRATION_ITEM, updateInspirationItemEffect);
}
